import { useRef } from "react";
import { updatePositionsAsync } from "../apiClient";
import { showDangerDialog, showSuccessAlert } from "../common";
import {
    Date_isSameDateAndTime,
    Date_isSameMinuteOnly,
    Date_toDateTimeString,
    Date_toTimeString,
    Date_withTimeString
} from "../common/dateUtils";
import { CodeStatus, PermitType, SignatureType } from "../common/enums";
import usePermitForm from "./usePermitForm";
import {
    CoordinationPermitFormState_default,
    CoordinationPermitFormState_populateErrorMessages,
    CoordinationPermitFormState_toRequest
} from "../types/forms/Permits/CoordinationPermitFormState";
import { PositionPickerRow_clearErrors, PositionPickerRow_toPosition } from "../types/forms/Permits/PositionPickerRow";
import { CoordinationPermit_toFormState } from "../types/permits/CoordinationPermit";
import { Signature_empty } from "../types/Signature";
import useDateInput from "./useDateInput";
import useSignature from "./useSignature";
import { PermitFormProps } from "../types/forms/Permits/PermitFormProps";

export default function useCoordinationPermitForm(props: PermitFormProps) {
    const baseformManager = usePermitForm({
        ...props,
        permitType: PermitType.CoordinationPermit,
        defaultFormState: () => CoordinationPermitFormState_default(props.baseUserInfo),
        populateErrorMessages: CoordinationPermitFormState_populateErrorMessages,
        toFormState: CoordinationPermit_toFormState,
        toRequest: CoordinationPermitFormState_toRequest,
        clearErrorMessages: (formState) => CoordinationPermitFormState_populateErrorMessages(formState, {})
    });

    const { storedPermit, isNewPermit, formState } = baseformManager;

    const startDateRef = useRef<HTMLInputElement>(null);
    const endDateRef = useRef<HTMLInputElement>(null);

    const startDateHandler = useDateInput({
        date: baseformManager.formState.startDate.value,
        ref: startDateRef,
        comparer: Date_isSameDateAndTime,
        toStringValue: Date_toDateTimeString,
        onDateChange: startDateTimeChangeHandler
    });

    const endTimeHandler = useDateInput({
        date: baseformManager.formState.endDate.value,
        ref: endDateRef,
        comparer: Date_isSameMinuteOnly,
        toStringValue: Date_toTimeString,
        stringParser: parseEndTimeString,
        onDateChange: endTimeChangeHandler
    });

    function startDateTimeChangeHandler(updatedStartDateTime: Date) {
        let updatedEndDate = new Date(updatedStartDateTime);
        updatedEndDate.setHours(formState.endDate.value.getHours(), formState.endDate.value.getMinutes(), 0, 0);

        if (updatedEndDate < updatedStartDateTime) {
            updatedEndDate = new Date(updatedStartDateTime);
        }

        baseformManager.registerFormStateChange((prevValue) => ({
            ...prevValue,
            startDate: { ...prevValue.startDate, value: updatedStartDateTime },
            endDate: { ...prevValue.endDate, value: updatedEndDate }
        }));
    }

    function endTimeChangeHandler(updatedEndDateTime: Date) {
        const startDate =
            formState.startDate.value > updatedEndDateTime
                ? new Date(updatedEndDateTime)
                : new Date(formState.startDate.value);

        baseformManager.registerFormStateChange((prevValue) => ({
            ...prevValue,
            endDate: { ...prevValue.endDate, value: updatedEndDateTime },
            startDate: { ...prevValue.startDate, value: startDate }
        }));
    }

    const permitProviderSignature = storedPermit
        ? storedPermit.permitProviderSignature
        : Signature_empty(SignatureType.PermitProvider);

    const permitProviderSignatureManager = useSignature({
        baseUserInfo: props.baseUserInfo,
        signature: permitProviderSignature,
        onRemoveSignature: async () => await baseformManager.removeSignatureManagerAsync(permitProviderSignature),
        onSaveSignature: async (signatureBlob, nameClarification) =>
            await baseformManager.createSignatureManagerAsync(
                signatureBlob,
                nameClarification,
                SignatureType.PermitProvider
            ),
        resources: props.resources,
        hideSignature: !!isNewPermit
    });

    function parseEndTimeString(updatedTimeString: string): Date {
        const endDate = Date_withTimeString(formState.startDate.value, updatedTimeString);

        return endDate;
    }

    async function updatePositionsClickHandlerAsync() {
        if (!storedPermit) {
            console.error("Attempted to updated positions with no stored permit in state.");
            return;
        }

        const updateResult = await updatePositionsAsync(
            storedPermit.entityId,
            formState.positions.map(PositionPickerRow_toPosition),
            PermitType.CoordinationPermit
        );

        if (updateResult.failed) {
            console.error(updateResult);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("RefreshPageAndTryAgain"));
            return;
        }

        showSuccessAlert(props.resources.get("PositionsUpdated"));
        baseformManager.setFormState((prevValue) => ({
            ...prevValue,
            positions: prevValue.positions.map((p) => PositionPickerRow_clearErrors(p))
        }));
    }

    const canAccept = storedPermit && storedPermit.canAccept;
    const canManagePositions = isNewPermit || (!!storedPermit && storedPermit.canUpdate);
    const canManagePositionActiveStatus =
        baseformManager.isNewPermit || (!!storedPermit && storedPermit.canManagePositionActiveStatus);

    const canManageStartDate =
        baseformManager.isNewPermit ||
        (!!storedPermit && storedPermit.status === CodeStatus.Registered && storedPermit.canUpdate);

    const showUpdatePositionsButton =
        storedPermit && storedPermit.status === CodeStatus.Accepted && canManagePositionActiveStatus;

    return {
        canAccept,
        canManagePositions,
        canManagePositionActiveStatus,
        showUpdatePositionsButton,
        canManageStartDate,
        permitProviderSignatureManager,
        startDateHandler,
        endTimeHandler,

        storedPermit: baseformManager.storedPermit,
        formState: baseformManager.formState,
        isLoading: baseformManager.isLoading,
        isNewPermit: baseformManager.isNewPermit,
        canCreate: baseformManager.canCreate,
        canUpdate: baseformManager.canUpdate,
        showAcceptUnsavedChangesModal: baseformManager.showAcceptUnsavedChangesModal,
        requestInProgress: baseformManager.requestInProgress,
        hasUnsavedChanges: baseformManager.hasUnsavedChanges,
        department: baseformManager.department,
        showRejectPermitModal: baseformManager.showRejectPermitModal,
        canManageContent: baseformManager.canManageContent,
        canManagePositionPolygons: baseformManager.canManagePositionPolygons,

        updatePositionsClickHandlerAsync,

        submitCreateHandlerAsync: baseformManager.submitCreateHandlerAsync,
        submitUpdateHandlerAsync: baseformManager.submitUpdateHandlerAsync,
        createSignatureManagerAsync: baseformManager.createSignatureManagerAsync,
        removeSignatureManagerAsync: baseformManager.removeSignatureManagerAsync,
        addPositionPickerRowHandler: baseformManager.addPositionPickerRowHandler,
        updatePositionPickerRowHandler: baseformManager.updatePositionPickerRowHandler,
        removePositionPickerRowHandler: baseformManager.removePositionPickerRowHandler,
        formStateChangeHandler: baseformManager.formStateChangeHandler,
        addParticipantHandler: baseformManager.addParticipantHandler,
        removeParticipantHandler: baseformManager.removeParticipantHandler,
        updateParticipantHandler: baseformManager.updateParticipantHandler,
        personFormElementChangeHandler: baseformManager.personFormElementChangeHandler,
        updatePermitHandlerAsync: baseformManager.updatePermitHandlerAsync,
        formKeyDownHandler: baseformManager.formKeyDownHandler,
        setShowAcceptUnsavedChangesModal: baseformManager.setShowAcceptUnsavedChangesModal,
        unsavedChangesModalUpdateHandlerAsync: baseformManager.unsavedChangesModalUpdateHandlerAsync,
        unsavedChangesModalAcceptHandlerAsync: baseformManager.unsavedChangesModalAcceptHandlerAsync,
        copyButtonClickHandler: baseformManager.copyButtonClickHandler,
        acceptButtonClickHandler: baseformManager.acceptButtonClickHandler,
        registerFormStateChange: baseformManager.registerFormStateChange,
        rejectPermitClickHandler: baseformManager.rejectPermitClickHandler,
        setShowRejectPermitModal: baseformManager.setShowRejectPermitModal,
        rejectPermitHandlerAsync: baseformManager.rejectPermitHandlerAsync
    };
}
